import { useEffect, useState } from 'react'
import { api } from './api'
import type { PesticideProduct } from './types'

/** What a scouting walk can find (server: SCOUTING_FINDINGS). */
export const SCOUTING_FINDINGS = [
  { value: 'swd', label: 'Spotted wing drosophila (SWD)' },
  { value: 'aphids', label: 'Aphids' },
  { value: 'mites', label: 'Mites' },
  { value: 'spiders', label: 'Spiders' },
  { value: 'mummy_berry', label: 'Mummy berry' },
  { value: 'other_disease', label: 'Other disease' },
  { value: 'waterlogging', label: 'Waterlogging' },
  { value: 'dying_plants', label: 'Dying plants' },
  { value: 'other', label: 'Something else' },
] as const
export type ScoutingFinding = (typeof SCOUTING_FINDINGS)[number]['value']

export const findingLabel = (f: string) => SCOUTING_FINDINGS.find((x) => x.value === f)?.label ?? f

/**
 * The pests a grower can look up registered products for, with the words labels use for them. Not
 * spiders (mostly predators), and not problems no product treats (waterlogging).
 */
export const PEST_LOOKUP: Partial<Record<ScoutingFinding, { term: string; short: string }>> = {
  swd: { term: 'spotted wing drosophila', short: 'SWD' },
  aphids: { term: 'aphid', short: 'aphids' },
  mites: { term: 'mite', short: 'mites' },
  mummy_berry: { term: 'mummy berry', short: 'mummy berry' },
}

/** Lookup by use case: the products registered against a pest on blueberries. */
export const pestLookupHref = (finding: ScoutingFinding) => {
  const pest = PEST_LOOKUP[finding]
  return pest ? `/pesticides?pest=${encodeURIComponent(pest.term)}&for=${encodeURIComponent(pest.short)}` : null
}

/** Current products registered for blueberries against `pest` (registry and checked labels). */
export function usePestProducts(pest: string | null) {
  const [items, setItems] = useState<PesticideProduct[]>([])
  const [loading, setLoading] = useState(!!pest)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!pest) return
    let cancelled = false
    setLoading(true)
    api.get<PesticideProduct[]>(`/pesticides?${new URLSearchParams({ pest, crop: 'BLUEBERRY', current: 'true', limit: '100' })}`).then(
      (found) => {
        if (cancelled) return
        setItems(found)
        setError(null)
        setLoading(false)
      },
      (err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Could not search the registry.')
        setLoading(false)
      },
    )
    return () => {
      cancelled = true
    }
  }, [pest])
  return { items, loading, error }
}
